import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Box,
  Button,
  CardContent,
  Card,
  Typography,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import qs from "qs";
import { useDispatch } from "react-redux";
import { setSubmitted, selectedProducts } from "redux/productParams";
import VirtualizedMultiSelect from "components/General/VirtualizedMultiSelect";
import GeoSelector from "components/General/GeoSelector";
import ProductTree from "components/product/ProductTree";

const PROVINCES = ["MB", "NB", "NL", "NS", "ON", "QC", "SK"];
const FALLBACK_GEO = {
  MB: ["5"],
  NB: ["22"],
  NL: ["22"],
  NS: ["22"],
  ON: ["1", "2", "5", "26"],
  QC: ["23", "24", "25", "26"],
  SK: ["2"],
};

const toTreeItems = (geo) =>
  Object.entries(geo).map(([province, districts]) => ({
    id: province,
    label: province,
    children: districts.map((d) => ({
      id: `${province}-${d}`,
      value: d,
      label: d,
    })),
  }));

const toDistricts = (geo) => [...new Set(Object.values(geo).flat())];

export default function ProductFilterForm({ sendDataToParent }) {
  const [products, setProducts] = useState([]);
  const [productselected, setProductselected] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);
  const [allStoresOptions, setAllStoresOptions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [geo, setGeo] = useState(null);
  const [allDistricts, setAllDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showProductTree, setShowProductTree] = useState(false);
  const [productTree, setProductTree] = useState(null);
  const [showDistrictsTree, setShowDistrictsTree] = useState(false);
  const dispatcher = useDispatch();

  useEffect(() => {
    axios
      .get("http://192.168.14.53:8000/api/productsDetail")
      .then((res) => setProducts(res.data));
    axios
      .get("http://192.168.14.53:8000/api/storesDetail", {
        params: { storeNumber: true },
      })
      .then((res) => {
        const list = res.data.nameNumber || res.data;
        setStoresOptions(list);
        setAllStoresOptions(list);
        setSelectedStores(list);
      });
    const apply = (g) => {
      const all = toDistricts(g);
      setGeo(g);
      setAllDistricts(all);
      setSelectedDistricts((prev) =>
        prev.length === 0 ? all : prev.filter((d) => all.includes(d)),
      );
    };
    axios
      .get("http://192.168.14.53:8000/api/provinceDistrictsGrouped")
      .then(
        (res) => res.data && Object.keys(res.data).length && apply(res.data),
      )
      .catch(() => apply(FALLBACK_GEO));
  }, []);

  useEffect(() => {
    if (!allStoresOptions.length || !allDistricts.length) return;
    const noDistricts = selectedDistricts.length === 0;
    const allSelected = selectedDistricts.length === allDistricts.length;

    if (noDistricts) {
      setStoresOptions([]);
      setSelectedStores([]);
      return;
    }
    if (allSelected) {
      setStoresOptions(allStoresOptions);
      return;
    }

    axios
      .get("http://192.168.14.53:8000/api/districtStoresDetail", {
        params: { districtId: selectedDistricts },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      })
      .then((res) => {
        setStoresOptions(res.data);
        setSelectedStores([]);
      });
  }, [selectedDistricts, allDistricts, allStoresOptions]);

  useEffect(() => {
    setSubmitEnabled(productselected.length > 0);
  }, [productselected]);

  const productNames = useMemo(
    () => new Map(products.map((p) => [String(p.product_id), p.product_name])),
    [products],
  );

  const toggleProductTree = () => {
    setShowProductTree((prev) => {
      const next = !prev;
      if (next && productTree === null) {
        axios
          .get("http://192.168.14.53:8000/api/productMappings")
          .then((res) => setProductTree(res.data || { mappings: [], sets: [] }))
          .catch(() => setProductTree({ mappings: [], sets: [] }));
      }
      return next;
    });
  };

  const handleProductChange = (values) => {
    const val = values || [];
    setProductselected(val);
    dispatcher(selectedProducts(val));
  };

  const handleStoresChange = (e) => setSelectedStores(e.target.value);

  const geographicItems = geo ? toTreeItems(geo) : [];

  const geographyActive =
    selectedDistricts.length !== allDistricts.length && allDistricts.length > 0;

  function handleSubmit(event) {
    event.preventDefault();
    dispatcher(setSubmitted(true));
    const provinces = geo
      ? Object.entries(geo)
          .filter(([, dists]) =>
            dists.some((d) => selectedDistricts.includes(d)),
          )
          .map(([p]) => p)
      : PROVINCES;
    sendDataToParent({
      startDate,
      endDate,
      stores: selectedStores,
      allStores:
        selectedStores.length === storesOptions.length &&
        storesOptions.length > 0,
      districts: selectedDistricts,
      provinces,
    });
  }
  const toggleDistrictsTree = () => {
    setShowDistrictsTree((prev) => {
      const next = !prev;
      if (next) setSelectedDistricts([...allDistricts]);
      return next;
    });
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Box ml={3} mr={3}>
        <Card>
          <CardContent>
            <Box pt={2} pb={2} ml={1}>
              <Grid container spacing={2}>
                <Grid item md={3} xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item md={3} xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Stores
                      {geographyActive && (
                        <Chip
                          label="Filtered by geography"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <VirtualizedMultiSelect
                      label="Stores"
                      id="stores-virtualized"
                      value={selectedStores}
                      options={storesOptions}
                      onChange={handleStoresChange}
                      width={600}
                      showSelectAll
                    />
                  </Box>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={1}>
                    <Button size="small" type="button" onClick={toggleDistrictsTree}>
                      {showDistrictsTree ? "Hide districts tree" : "Show districts tree"}
                    </Button>

                    {showDistrictsTree && geo && (
                      <GeoSelector
                        title="Provinces & districts"
                        items={geographicItems}
                        selected={selectedDistricts}
                        onChange={setSelectedDistricts}
                        width={300}
                      />
                    )}
                    {geographyActive && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setSelectedDistricts([...allDistricts])}
                      >
                        Clear geography filter
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Products
                    </Typography>
                    <Button size="small" onClick={toggleProductTree}>
                      {showProductTree
                        ? "Hide products tree"
                        : "Show products tree"}
                    </Button>
                    {showProductTree &&
                      (productTree ? (
                        <ProductTree
                          mappings={productTree.mappings}
                          sets={productTree.sets}
                          productNames={productNames}
                          selected={productselected}
                          onChange={handleProductChange}
                        />
                      ) : (
                        <Typography variant="body2">Loading...</Typography>
                      ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box pt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={!submitEnabled}
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </form>
  );
}
